import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, X } from "lucide-react";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BRAZILIAN_STATES = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
];

interface SearchBarProps {
  variant?: "hero" | "compact";
}

export function SearchBar({ variant = "hero" }: SearchBarProps) {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");
  const [state, setState] = useState(searchParams.get("estado") || "");
  const [city, setCity] = useState(searchParams.get("cidade") || "");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.set("q", keyword);
    if (state) params.set("estado", state);
    if (city) params.set("cidade", city);
    navigate(`/vagas?${params.toString()}`);
  };

  const handleStateChange = (value: string) => {
    setState(value);
    setCity(""); // Limpa cidade quando estado muda
  };

  const clearKeyword = () => setKeyword("");
  const clearState = () => {
    setState("");
    setCity("");
  };
  const clearCity = () => setCity("");

  if (variant === "compact") {
    return (
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar vagas..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button type="submit">Buscar</Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSearch} className="w-full max-w-5xl mx-auto px-4 sm:px-0">
      <div className="bg-card rounded-2xl shadow-xl p-4 sm:p-5 border border-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Campo Palavra-chave - Ocupa linha inteira em mobile, metade em tablet, 1 coluna em desktop */}
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Cargo, empresa ou palavra-chave"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-12 pr-10 h-12 border border-border rounded-xl bg-background focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-base"
          />
          {keyword && (
            <button
              type="button"
              onClick={clearKeyword}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted transition-colors"
              aria-label="Limpar palavra-chave"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Campo Estado */}
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none" />
          <Select value={state} onValueChange={handleStateChange}>
            <SelectTrigger className="pl-12 pr-10 h-12 border border-border rounded-xl bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary text-base [&>span]:text-left [&>svg]:hidden data-[placeholder]:text-muted-foreground">
              <SelectValue placeholder="Selecione o estado" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {BRAZILIAN_STATES.map((s) => (
                <SelectItem key={s.sigla} value={s.sigla}>
                  {s.sigla} - {s.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {state && (
            <button
              type="button"
              onClick={clearState}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted transition-colors z-10"
              aria-label="Limpar estado"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Campo Cidade */}
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none" />
          <CityAutocomplete
            value={city}
            onChange={setCity}
            placeholder={state ? "Selecione a cidade" : "Selecione o estado"}
            variant="embedded"
            className="w-full pr-10 h-12 border border-border rounded-xl bg-background focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary"
            disabled={!state}
            state={state}
          />
          {city && (
            <button
              type="button"
              onClick={clearCity}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted transition-colors z-10"
              aria-label="Limpar cidade"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Botão - Ocupa linha inteira em mobile/tablet, 1 coluna em desktop */}
        <Button type="submit" size="lg" className="h-12 sm:col-span-2 lg:col-span-1">
          Buscar Vagas
        </Button>
      </div>
    </form>
  );
}
